import {
  type ApolloClient,
  type ApolloQueryResult,
  disableFragmentWarnings,
  type NormalizedCacheObject,
} from "@apollo/client";
import type { Urls } from "@selector/shared";
import { TOKEN_KEY } from "@selector/shared/settings";
import { path, split } from "ramda";
import { storage } from "widgets";

import type { FileDownloadStatus } from "@/modules/notifications/utils/types.ts";

import { cleanupGraphQLErrorMessage } from "../app-utils/errors.js";
import { stringifyLikeJson } from "../app-utils/graphql.js";
import { futch, type ProgressCallback } from "../app-utils/upload.js";
import type {
  AaFeature,
  AdminBrowser,
  AdvancedSearchPreview,
  AnalyzerIdMapping,
  AppConfiguration,
  Attribute,
  AttributeDefaultValueMapEntry,
  AttributeValue,
  AuditFilterOption,
  BamSummary,
  BlastIndex,
  BlastJob,
  Capability,
  Configuration,
  ContentBrowser,
  Contig,
  ContigSearch,
  CoordinateList,
  Coverage,
  Dashboard,
  DataSeries,
  Dataset,
  EditSamplesGrid,
  FileUploadResult,
  FullPileupAlignment,
  Gene,
  GenMutation,
  GenomeAssembly,
  GenomeBrowserStatistics,
  GenomeSearch,
  GenomicFeature,
  Group,
  GroupList,
  InputAddProjectMemberInput,
  InputAdvancedSearchInput,
  InputAnalyzerIdMappingInput,
  InputAttributeInput,
  InputAttributeValueInput,
  InputColumnInput,
  InputCoordinateListInput,
  InputCreateGroupInput,
  InputDashboardInput,
  InputEditParentInput,
  InputEditSamplesInput,
  InputGenomeAssembliesInput,
  InputJobInput,
  InputJobValidation,
  InputModifyOrganismInput,
  InputModifyPlaybookEventInput,
  InputModifyProjectFileInput,
  InputModifyProjectInput,
  InputModifySavedItemInput,
  InputNfParamInput,
  InputOrganismPropertyTypeInput,
  InputParentInput,
  InputParentTypeInput,
  InputPlaybookCategoryInput,
  InputPlaybookEventInput,
  InputPlaybookInput,
  InputRegionInput,
  InputRegionWithoutStrand,
  InputRemoveProjectMemberInput,
  InputRoleInput,
  InputSamplesValidationInputData,
  InputSampleTypeInput,
  InputSearchEntityTypeInput,
  InputSequenceIdentifiersInput,
  InputSequenceUploadInput,
  InputSequenceUploadValidation,
  InputSetComparisonGroupInput,
  InputSetComparisonInput,
  InputTableQuerySpec,
  InputUpdateProjectMemberRoleInput,
  InputUserAccountInput,
  InputUserGroupInput,
  InputUserParamsInput,
  InputUserPasswordChangeRequestInput,
  InputViewInput,
  InputWorkflowParamInput,
  InputWorkflowViewParamInput,
  JobResult,
  License,
  Marker,
  MutationGroupByTypeArgs,
  MutationGroupInLegacyPortalArgs,
  NfWorkflow,
  Notification,
  NuFeature,
  OraclePasswordLimit,
  Organism,
  OrganismPhenotype,
  OrganismProperty,
  OrganismPropertyType,
  Parent,
  ParentStatistic,
  ParentType,
  PileupRow,
  Playbook,
  PlaybookCategory,
  PlaybookEvent,
  Project,
  ProjectActivity,
  ProjectFile,
  ProjectFileSearch,
  ProjectList,
  ProjectMember,
  Protein,
  Range,
  RawAttributeDefaultMapEntry,
  RecentItem,
  RegionFlag,
  Role,
  RowIndex,
  Sample,
  SampleCount,
  SampleStatus,
  SampleType,
  SavedItem,
  SavedItemsItemsArgs,
  SavedItemsListing,
  SearchAttribute,
  SearchResult,
  SearchScope,
  SearchType,
  Sequence,
  SequenceFeatureTypes,
  SequenceGroupTypeCount,
  SequenceType,
  ServerStatusInfo,
  SetComparison,
  Table,
  Taxonomy,
  Template,
  Tool,
  TrackFeatures,
  TrackGroupIndicator,
  TrackNuProfile,
  TrackVariants,
  Transcript,
  User,
  UserGroup,
  UserGroupItemValidationResult,
  UserParams,
  ValidationError,
  VariantAnalysisGroup,
  View,
  Workflow,
  WorkflowP2,
} from "../graphql.js";
import type { IAuditLogPayload } from "../redux/modules/auth.js";
import type { IDownloadFileInput, ITableDownloadFileParams } from "../redux/modules/data-export.js";
import type { IClassicGroup, ITableColumn } from "../redux/modules/table-data.js";
import { AUTH_KEY, TRACK_TYPE, X_AUTH_KEY } from "../settings/settings.js";
import type { ITableSpecWithContext } from "../ui-modules/content-browser/ContentBrowserTableView.js";
import type { IUserAccount } from "../ui-modules/core/CreateAccountPasswordPage.js";
import type { IProjectsListCardConfig } from "../ui-modules/dashboard/ProjectsDashboardCard.js";
import type { IRecentActivitiesCardConfig } from "../ui-modules/dashboard/RecentActivitiesDashboardCard.js";
import type { ISampleStatusesCardConfig } from "../ui-modules/dashboard/SampleStatusDashboardCard.js";
import type { ISavedItemsCardConfig } from "../ui-modules/dashboard/SavedItemsDashboardCard.js";
import FileDownloadClient from "./FileDownloadClient.js";
import {
  generateRestToken,
  getAddProjectMemberMutation,
  getChangeYourPasswordQueryOptions,
  getCopyFilesOptions,
  getCopyFlags,
  getCopyGroups,
  getCopyViews,
  getCreateAdvancedSearchResultMutationOptions,
  getCreateAttributeQueryOptions,
  getCreateGroupFromSetComparison,
  getCreateGroupInClassicPortal,
  getCreateGroupMutationOptions,
  getCreateGroupsUnionQueryOptions,
  getCreateProjectCommentQueryOptions,
  getCreateProjectFolderQueryOptions,
  getCreateProjectHyperlink,
  getCreateProjectMutationOptions,
  getCreateRoleQueryOptions,
  getCreateSampleGroupFromParentsUnionQueryOptions,
  getCreateSearchResultMutationOptions,
  getCreateTempGroupQueryOptions,
  getCreateUsergroupMutationOptions,
  getCreateUsersQueryOptions,
  getCreateViewMutationOptions,
  getDeleteActivitiesQueryOptions,
  getDeleteAttributesQueryOptions,
  getDeleteColumnTemplateQueryOptions,
  getDeleteCoordinateListsQueryOptions,
  getDeleteFlagsQueryOptions,
  getDeleteGroupsQueryOptions,
  getDeleteOrgPropertyTypesQueryOptions,
  getDeleteParentsQueryOptions,
  getDeleteParentTypesQueryOptions,
  getDeletePlaybookCategory,
  getDeleteProjectQueryOptions,
  getDeleteSampleTypesQueryOptions,
  getDeleteUploadedSequencesQueryOptions,
  getDeleteUserGroupQueryOptions,
  getDeleteViewsQueryOptions,
  getEditOrganismQueryOptions,
  getGroupRelated,
  getIndexBamFileOptions,
  getInstallNcbiGenomes,
  getInvalidatePasswordsQueryOptions,
  getLogoutMutationOptions,
  getModifyAttributeQueryOptions,
  getModifyParentTypeQueryOptions,
  getModifyPlaybookCategory,
  getModifyPlaybookEvent,
  getModifyPlaybookQueryOptions,
  getModifyProjectFileOptions,
  getModifyProjectMutationOptions,
  getModifySampleTypeQueryOptions,
  getModifyUserGroupQueryOptions,
  getModifyUserQueryOptions,
  getMoveFilesOptions,
  getRemoveProjectMemberMutation,
  getRunPlaybookQueryOptions,
  getSaveColumnTemplateQueryOptions,
  getSaveDashboardMutationOptions,
  getSaveEditedSampleQueryOptions,
  getSaveNewPlaybookCategory,
  getSaveNewPlaybookQueryOptions,
  getSaveOrgPropertyTypeQueryOptions,
  getSaveParamViewInputOptions,
  getSaveParentQueryOptions,
  getSaveParentTypeQueryOptions,
  getSaveSampleTypeQueryOptions,
  getSetSampleLockingQueryOptions,
  getSubmitCoordinatesListUploadMutationOptions,
  getSubmitJobMutationOptions,
  getSubmitNextflowPlaybookOptions,
  getSubmitSequencesUploadMutationOptions,
  getUpdateActiveTemplateQueryOptions,
  getUpdateColumnTemplateQueryOptions,
  getUpdateNcbiGenomeListOptions,
  getUpdateProjectRoleMutation,
  getUpdateRoleQueryOptions,
  getUpdateSavedItemOptions,
  getUpdateUserParamsMutationOptions,
  type ICustomMutationOptions,
  setAllNotificationsAsRead,
  setNotificationAsRead,
} from "./mutations.js";
import {
  fetchProjectFilesForAttachmentsQueryOptions,
  getAaFeatureQueryOptions,
  getAccessibleUserGroups,
  getAdminBrowserInfoQueryOptions,
  getAdminBrowserListingQueryOptions,
  getAdminParentTypeQueryOptions,
  getAdminParentTypesQueryOptions,
  getAdminSampleStatusSetsQueryOptions,
  getAdminSampleTypeQueryOptions,
  getAdminSampleTypesQueryOptions,
  getAdvancedSearchAttributesQueryOptions,
  getAdvancedSearchPreviewQueryOptions,
  getAdvancedSearchTypesQueryOptions,
  getAllAdminTools,
  getAllTaxonomies,
  getAttributeQueryOptions,
  getAttributesAsDecorationQueryOptions,
  getAttributesQueryOptions,
  getAvailableDatasetsQueryOptions,
  getBamFileSummaryQueryOptions,
  getBedFeaturesQueryOptions,
  getBlastIndexListQueryOptions,
  getCheckAttributeUniquenessQueryOptions,
  getCheckSequencedOrganismsOptions,
  getCheckVirtualGenomeExistsOptions,
  getColumnQueryOptions,
  getConfigurationQueryOptions,
  getContentBrowserCountsQueryOptions,
  getContentBrowserInfoQueryOptions,
  getContentBrowserListingQueryOptions,
  getContigForGenomeBrowserQueryOptions,
  getContigSearchResultsQueryOptions,
  getContigSummaryQueryOptions,
  getCoordinateFeaturesQueryOptions,
  getCoordinateListsByGenomeQueryOptions,
  getCoordinateListsQueryOptions,
  getCurrentUserCapabilitiesQueryOptions,
  getCurrentUserQueryOptions,
  getDashboardCardProjectsListQueryOptions,
  getDashboardCardRecentActivitiesListQueryOptions,
  getDashboardCardSavedItemsQueryOptions,
  getDashboardListQueryOptions,
  getDataSeriesQueryOptions,
  getDataSeriesSearchQueryOptions,
  getDatasetMinMaxValuesQueryOptions,
  getDatasetQueryOptions,
  getDataTableQueryOptions,
  getDefaultDatasetIdQueryOptions,
  getEventForRerunQueryOptions,
  getEventQueryOptions,
  getFetchAdminPlaybookOptions,
  getFetchAdminPlaybooksOptions,
  getfetchAllOrgPropertyTypesQueryOptions,
  getfetchApplicableOrgPropertyTypesQueryOptions,
  getFetchApprovedWorkflowsOptions,
  getFetchAuditLogExportFilterOptionsQueryOptions,
  getFetchBasicUserGroups,
  getFetchNfPlaybookOptions,
  getFetchNfWorkflowOptions,
  getFetchNfWorkflowsOptions,
  getfetchOrgPropertyTypeQueryOptions,
  getFetchP2WorkflowOptions,
  getFetchPhenotypeValuesQueryOptions,
  getFetchPlaybookCategories,
  getFetchPlaybookCategoryById,
  getFetchPlaybookOptions,
  getFetchPlaybooksOptions,
  getFetchPublishedWorkflowsOptions,
  getFetchSampleIdsForParentIdOptions,
  getFetchSampleTypeStatusOptions,
  getFetchSequenceFeatureTypesQueryOptions,
  getFetchSequenceTypesQueryOptions,
  getFetchSystemDetailsOptions,
  getFetchTestGenomicGeneOptions,
  getFetchTrackCommunityAnnotationsQueryOptions,
  getFetchUserGroupsQueryOptions,
  getFetchValuesForOrganismPropertyTypeQueryOptions,
  getFileNameExistsInCollector,
  getFileNameExistsOptions,
  getGeneQueryOptions,
  getGenMutationQueryOptions,
  getGenomeAndSequenceStatisticsQueryOptions,
  getGenomeAssemblies,
  getGenomeGffFeatureQueryOptions,
  getGenomeRelatedProjectsQueryOptions,
  getGenomeSearchQueryOptions,
  getGenomeSummaryQueryOptions,
  getGenomeViewerFilesByIdOption,
  getGenomeViewGenomeQueryOptions,
  getGroupDataSeriesQueryOptions,
  getGroupDataSeriesSearchQueryOptions,
  getGroupDatasetQueryOptions,
  getIsAcceptableNewOracleAccountNameQueryOptions,
  getIsParentNameUniqueQueryOptions,
  getJobForRerunQueryOptions,
  getJobQueryOptions,
  getJobResultsQueryOptions,
  getLightDatasetQueryOptions,
  getLoadAllCapabilitiesQueryOptions,
  getLoadAllRolesQueryOptions,
  getLoadColumnTemplateQueryOptions,
  getLoadCompatibleVariantGroupsOptions,
  getLoadGroupsOptions,
  getLoadP2WorkflowForParamsValidationOptions,
  getLoadVariantAnalysisGroupOptions,
  getMarkerQueryOptions,
  getMinimalParentTypesQueryOptions,
  getMutantGenomesByAnalysisGroupQueryOptions,
  getNgsCoverageFeaturesQueryOptions,
  getNgsPileupFeaturesQueryOptions,
  getNgsReadTooltipQueryOptions,
  getNotificationListQueryOptions,
  getNotificationQueryOptions,
  getNuFeatureQueryOptions,
  getNumberOfFreeSeatsQueryOptions,
  getOrganismsFromProjectFileByIdOptions,
  getParentTypeExistsQueryOptions,
  getParentTypesForSampleTypeCreationQueryOptions,
  getPlaybookExistsQueryOptions,
  getPossibleLdapUsersQueryOptions,
  getProfileFeaturesQueryOptions,
  getProjectActivitiesQueryOptions,
  getProjectActivityQueryOptions,
  getProjectCapabilitiesListQueryOptions,
  getProjectListQueryOptions,
  getProjectQueryOptions,
  getProjectSequencedOrganismsQueryOptions,
  getProteinQueryOptions,
  getRawSampleAttributeDefaultsQueryOptions,
  getRecentEventsQueryOptions,
  getRelatedAnalysisGroupsQueryOptions,
  getSampleAttributeDefaultsQueryOptions,
  getSampleEditingColumnQueryOptions,
  getSampleEditingDataQueryOptions,
  getSampleParentQueryOptions,
  getSampleParentStatisticsQueryOptions,
  getSampleQueryOptions,
  getSampleStatusesQueryOptions,
  getSampleTypeExistsQueryOptions,
  getSampleTypeForPlaybooksButton,
  getSampleTypesQueryOptions,
  getSampleTypeWizardQueryOptions,
  getSampleValuesQueryOptions,
  getSavedGroupQueryOptions,
  getSavedItemsForAttachmentsQueryOptions,
  getSavedItemsListingQueryOptions,
  getSavedItemsQueryOptions,
  getSavedViewQueryOptions,
  getSearchGenomesForSamplesQueryOptions,
  getSearchProjectFilesOptions,
  getSearchResultQueryOptions,
  getSearchScopeOptions,
  getSequencedGenomeSearchQueryOptions,
  getSequenceGroupTypesQueryOptions,
  getSequenceIdentifiersQueryOptions,
  getSequenceQueryOptions,
  getSetComparison,
  getTableDataRowPositionQueryOptions,
  getTrackExpressionValuesQueryOptions,
  getTrackFeaturesQueryOptions,
  getTrackMarkerQueryOptions,
  getTrackNuAaFeaturesQueryOptions,
  getTrackVariantsQueryOptions,
  getTrackVcfVariantsQueryOptions,
  getTranscriptQueryOptions,
  getTranslateAnalyzerIdsOptions,
  getUserGroupAdminCountsQueryOptions,
  getUserParamsQueryOptions,
  getUsersForAdminQueryOptions,
  getValidateBamFileAgainstRefGenomesOptions,
  getValidateBedFileAgainstRefGenomesOptions,
  getValidateBlastInputSequenceQueryOptions,
  getValidateFlagsAgainstProjectUserGroupOptions,
  getValidateProjectNameQueryOptions,
  getValidateSavedGroupsAgainstProjectUserGroupOptions,
  getValidateSavedViewsAgainstProjectUserGroupOptions,
  getValidateSequenceUploadQueryOptions,
  getValidateSingleGroupAgainstProjectUserGroupOptions,
  getValidateSingleViewAgainstProjectUserGroupOptions,
  getValidateUsergroupNameQueryOptions,
  getVariantRelatedGenomes,
  hasInternetConnectionOptions,
  type IQueryOptions,
  isValidGenomeIdentifier,
  isValidGenomeName,
  resolveTemplateCount,
  resolveTemplateNext,
  resolveTemplateNextElement,
} from "./queries.js";
import { getProjectFilesDataForSendingToGenomeViewerOptions, type ISyncQueryOptions } from "./synchronousQueries.js";

const DEFAULT_LIMIT_100 = 100;
const DEFAULT_LIMIT_1000 = 1000;

export interface IAuthTokenResponse {
  token: string;
}

export type IUploadFileResponse = FileUploadResult;

export interface IDownloadFileParams {
  id: number;
  name: string;
  extension: string;
}

export interface ISequenceExportInput extends ITableDownloadFileParams {
  region: InputRegionInput;
  maxLength: number;
  headerFormat: string;
  compressed: boolean;
  reverseComplement: boolean;
}

export interface IInfo {
  application: AppConfiguration;
  license: License;
  oraclePasswordLimits: OraclePasswordLimit;
}

export interface IDownloadGDAFileParams extends IDownloadFileParams {
  columns: string[];
  annotations: string[];
  dataSeries: string[];
}

// use context prop to detect requests from admin page
// for admin page requests, more should be listed compared to the default case (eg, genomes)
export interface IInputTableQuerySpecWithContext extends InputTableQuerySpec {
  context?: string;
}

export interface IDataTableQueryParams {
  tableQuerySpec: IInputTableQuerySpecWithContext;
  withColumns?: boolean;
  withRowIds?: boolean;
  withPagination?: boolean;
  withPageRows?: boolean;
  withTranscripts?: boolean;
  withProteins?: boolean;
  withGenes?: boolean;
  withTranscriptLocations?: boolean;
  withProteinLocations?: boolean;
  withGenomeStatistics?: boolean;
  withProteinMutations?: boolean;
  withGeneMutations?: boolean;
  withRefVariant?: boolean;
  withRefRegion?: boolean;
  withAnalysisGroup?: boolean;
  withSequence?: boolean;
  // entityType?: string;
  withLiteratureReferences?: boolean;
  withOtherReferences?: boolean;
  withPFam?: boolean;
  withInternalReferences?: boolean;
  withPathways?: boolean;
  withLowerNeighborGenes?: boolean;
  withHigherNeighborGenes?: boolean;
  withLowerNeighborVariants?: boolean;
  withHigherNeighborVariants?: boolean;
  withBlastResults?: boolean;
  withMutVariants?: boolean;
  withMutProteinMutations?: boolean;
  withSamples?: boolean;
}

// Create Project
export interface ICreateProjectParams {
  name: string;
  isPublic: boolean;
  description: string;
  userNames: string[];
  emailNotification: boolean;
  userGroupId: number;
  capabilityIds?: number[];
}

// Create Group
export interface ICreateGroupParams extends InputCreateGroupInput {
  project: Project;
}

export interface ISyncResponse<T> {
  data: T;
  errors: Error[];
}

export interface ICanCancel {
  isCanceled?: boolean;
}

export default class WebApiClient {
  private urls: Urls;
  private apolloClient: ApolloClient<NormalizedCacheObject>;
  private fileDownloadClient: FileDownloadClient;

  public static NETWORK_ERROR_MESSAGE =
    "Cannot connect to the server, please make sure you are connected to the network.";
  public static DOWNLOAD_IFRAME_TARGET_NAME = FileDownloadClient.DOWNLOAD_IFRAME_TARGET_NAME;

  constructor(urls: Urls, apolloClient: ApolloClient<NormalizedCacheObject>) {
    this.urls = urls;
    this.apolloClient = apolloClient;
    this.fileDownloadClient = new FileDownloadClient(this.urls.api, () => this.authHeaders());
    disableFragmentWarnings();

    this.handleGraphQLError = this.handleGraphQLError.bind(this);
    this.checkHttpResponseStatus = this.checkHttpResponseStatus.bind(this);
    this.checkErrors = this.checkErrors.bind(this);
  }

  private authHeaders(): Record<string, string> {
    const token = storage[TOKEN_KEY];

    if (token) {
      return {
        [AUTH_KEY]: `Bearer ${token}`,
        [X_AUTH_KEY]: `Bearer ${token}`,
      };
    }

    return {};
  }

  public unwatchFileDownload(fileKey) {
    this.fileDownloadClient.unwatchFileDownload(fileKey);
  }

  public updateFileDownloadStatus(fileKey, status: FileDownloadStatus) {
    this.fileDownloadClient.updateFileDownloadStatus(fileKey, status);
  }

  public getFileDownloadPromise(fileKey) {
    return this.fileDownloadClient.getFileDownloadPromise(fileKey);
  }

  /************** GRAPHQL queries ******************/
  public executeGraphQLQueryWithCancel<R>(getQueryOptions: () => IQueryOptions): Promise<R> & ICanCancel {
    return this.executeGraphQLQuery(getQueryOptions) as Promise<R> & ICanCancel;
  }

  public executeGraphQLQuery<R>(getQueryOptions: () => IQueryOptions): Promise<R> {
    type GQLData = any;

    let queryOptions: IQueryOptions;

    try {
      queryOptions = {
        ...getQueryOptions(),
        fetchPolicy: "no-cache",
      };
    } catch (error) {
      return new Promise<R>((_resolve, reject) => reject(error));
    }

    return this.apolloClient.query(queryOptions).then(
      (graphQLResult: ApolloQueryResult<GQLData>): R => {
        // we get here only if there is no error
        const { data } = graphQLResult;

        if (queryOptions.rootField) {
          return path<GQLData>(split(".", queryOptions.rootField), data);
        }

        // if rootField is null, that means we directly return the data as-is (e.g. in case we get an Array)
        return data as R;
      },
      (error) => this.handleGraphQLError<R>(error),
    );
  }

  public executeGraphQLMutation<R>(getMutationOptions: () => ICustomMutationOptions): Promise<R> {
    type GQLData = any;

    let mutationOptions: ICustomMutationOptions;

    try {
      mutationOptions = getMutationOptions();
    } catch (error) {
      return new Promise<R>((_resolve, reject) => reject(error));
    }

    return this.apolloClient.mutate(mutationOptions).then(
      (graphQLResult: ApolloQueryResult<GQLData>): R => {
        // we get here only if there is no error
        const { data } = graphQLResult;

        if (mutationOptions.rootField) {
          return path<GQLData>(split(".", mutationOptions.rootField), data);
        }

        // if rootField is null, that means we directly return the data as-is (e.g. in case we get an Array)
        return data as R;
      },
      (error) => this.handleGraphQLError<R>(error),
    );
  }

  // in some cases a graphQL synchronous request is required (e.g. for validation before opening a new tab/window)
  public executeGraphQLQuerySync(getQueryOptions: () => ISyncQueryOptions): ISyncResponse<any> {
    const queryOptions: ISyncQueryOptions = getQueryOptions();

    const request = new XMLHttpRequest();

    request.open("POST", `${this.urls.api}/graphql`, false); // Synchronous request
    request.withCredentials = true;
    request.setRequestHeader("Content-Type", "application/json");

    const authHeaders = this.authHeaders();

    for (const key in authHeaders) {
      request.setRequestHeader(key, authHeaders[key]);
    }

    request.send(JSON.stringify(queryOptions));

    if (request.status === 200) {
      const graphQLResponse = JSON.parse(request.responseText);
      if (queryOptions.rootField) {
        graphQLResponse.data = path(split(".", queryOptions.rootField), graphQLResponse.data);
      }
      return graphQLResponse;
    }

    return { data: null, errors: [{ name: request.statusText, message: request.responseText }] };
  }

  /************** REST Queries ******************/

  public fetchAuthTokenEx(token: string): Promise<IAuthTokenResponse> {
    return fetch(`${this.urls.api}/loginExternal`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token: token }),
    })
      .then(this.checkHttpResponseStatus)
      .then((response: Response): Promise<unknown> => response.json())
      .then(this.checkErrors)
      .then((json: unknown) => json.data);
  }

  /************** REST Queries ******************/

  public fetchAuthToken(username: string, password: string): Promise<IAuthTokenResponse> {
    return fetch(`${this.urls.api}/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userName: username.trim(), password: password.trim() }),
    })
      .then(this.checkHttpResponseStatus)
      .then((response: Response): Promise<unknown> => response.json())
      .then(this.checkErrors)
      .then((json: unknown) => json.data);
  }

  public fetchInfo(): Promise<IInfo> {
    return fetch(`${this.urls.api}/info`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    })
      .then(this.checkHttpResponseStatus)
      .then((response: Response): Promise<Configuration> => response.json())
      .then(this.checkErrors);
  }

  public isChangePasswordTokenValid(token: string): Promise<IUserAccount> {
    return fetch(`${this.urls.api}/canChangePassword`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token: token }),
    })
      .then(this.checkHttpResponseStatus)
      .then((response: Response): Promise<IUserAccount> => response.json())
      .then(this.checkErrors);
  }

  public setPassword(payload): Promise<boolean> {
    return fetch(`${this.urls.api}/changePassword`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(this.checkHttpResponseStatus);
  }

  public uploadFile(
    endpoint: string,
    formData: FormData,
    progressCallback?: ProgressCallback,
    requestStartedCallback?: (XMLHttpRequest) => void,
  ): Promise<IUploadFileResponse | ProjectFile> {
    return futch(
      this.urls.api + endpoint,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          enctype: "multipart/form-data",
          ...this.authHeaders(),
        },
        body: formData,
      },
      progressCallback,
      requestStartedCallback,
    )
      .then((response) => JSON.parse(response))
      .then(this.checkErrors)
      .then((json) => json.data);
  }

  public getFileContent(endpoint: string, inputParams: any): Promise<string> {
    return fetch(this.urls.api + endpoint, {
      method: "POST",
      credentials: "include",
      headers: {
        ...this.authHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(inputParams),
    }).then((response) => response.text());
  }

  public downloadFileAndWatchCompletion(endpoint: string, inputParams: IDownloadFileInput): Promise<boolean> {
    return this.fileDownloadClient.downloadFileAndWatchCompletion(endpoint, inputParams);
  }

  public auditLog(auditLogPayload: IAuditLogPayload): Promise<Response> {
    const payload: IAuditLogPayload = {
      ...auditLogPayload,
      message: auditLogPayload.message,
    };

    if (auditLogPayload.payload && typeof auditLogPayload.payload !== "string") {
      payload.payload = stringifyLikeJson(auditLogPayload.payload);
    }

    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...this.authHeaders(),
    };

    if (!headers[AUTH_KEY]) {
      return Promise.resolve(null);
    }

    return fetch(`${this.urls.api}/auditLog`, {
      method: "POST",
      credentials: "include",
      headers: headers,
      body: JSON.stringify(payload),
    });
  }

  /************** response handling UTILS ******************/

  private handleGraphQLError<T>(error) {
    if (error.networkError) {
      return new Promise<T>((_resolve, reject) => reject({ ...error, message: WebApiClient.NETWORK_ERROR_MESSAGE }));
      //return Promise.reject({...error, message: WebApiClient.NETWORK_ERROR_MESSAGE});
    }

    return new Promise<T>((_resolve, reject) =>
      reject({
        ...error,
        message: cleanupGraphQLErrorMessage(error.message),
      }),
    );
  }

  private checkHttpResponseStatus(response: Response): Response | Promise<any> {
    if (response.ok) {
      return response;
    }

    return response.text().then((text) => {
      if (import.meta.env.DEV) {
        // DO NOT DELETE, see SEL9-532
        // log the error code in console for dev, but do not show it in the reject
        // because it will pop up in a notification
        console.error(`${response.status} : ${response.statusText}`);
      }
      throw new Error(text || `${response.statusText}`);
    });
  }

  private checkErrors(json: any): Promise<any> {
    if (json?.errors && json.errors.length > 0) {
      return Promise.reject(json.errors[0]);
    }

    return json;
  }

  /**
   ******************************   QUERIES ******************************
   */

  public loadGroups(type: string): Promise<GroupList> {
    return this.executeGraphQLQuery<GroupList>(() => getLoadGroupsOptions(type));
  }

  public loadVariantAnalysisGroup(type: string, groupId: number, variantId: number): Promise<VariantAnalysisGroup> {
    return this.executeGraphQLQuery<VariantAnalysisGroup>(() =>
      getLoadVariantAnalysisGroupOptions(type, groupId, variantId),
    );
  }

  public loadCompatibleVariantGroups(analysisGroupId: number): Promise<GroupList> {
    return this.executeGraphQLQuery<GroupList>(() => getLoadCompatibleVariantGroupsOptions(analysisGroupId));
  }

  public fetchGene(geneId: number): Promise<Gene> {
    // GeneSummary
    return this.executeGraphQLQuery<Gene>(() => getGeneQueryOptions(geneId));
  }

  public fetchUserParams(): Promise<UserParams> {
    return this.executeGraphQLQuery(() => getUserParamsQueryOptions());
  }

  public fetchCurrentUserData(): Promise<User> {
    return this.executeGraphQLQuery(() => getCurrentUserQueryOptions());
  }

  public fetchCurrentUserCapabilities(): Promise<User> {
    return this.executeGraphQLQuery(() => getCurrentUserCapabilitiesQueryOptions());
  }

  public fetchUsersForAdmin(): Promise<User[]> {
    return this.executeGraphQLQuery(() => getUsersForAdminQueryOptions());
  }

  public fetchPossibleLdapUsersForAdmin(): Promise<User[]> {
    return this.executeGraphQLQuery(() => getPossibleLdapUsersQueryOptions());
  }

  public fetchGenomeBrowserGenome(genomeId: number, contigLimit: number = DEFAULT_LIMIT_100): Promise<Organism> {
    return this.executeGraphQLQuery(() => getGenomeViewGenomeQueryOptions(genomeId, contigLimit));
  }

  public fetchGenomeAndSequenceStatistics(genomeId: number): Promise<GenomeBrowserStatistics> {
    return this.executeGraphQLQuery(() => getGenomeAndSequenceStatisticsQueryOptions(genomeId));
  }

  public fetchDefaultDatasetId(genomeId: number): Promise<number> {
    return this.executeGraphQLQuery(() => getDefaultDatasetIdQueryOptions(genomeId));
  }

  public fetchDataset(datasetId = -1): Promise<Dataset> {
    return this.executeGraphQLQuery(() => getDatasetQueryOptions(datasetId));
  }

  public fetchLightDataset(datasetId: number): Promise<Dataset> {
    return this.executeGraphQLQuery(() => getLightDatasetQueryOptions(datasetId));
  }

  public fetchDatasetMinMaxValues(datasetId: number): Promise<Dataset> {
    return this.executeGraphQLQuery(() => getDatasetMinMaxValuesQueryOptions(datasetId));
  }

  public fetchGroupDataset(tableSelectionSpec: InputTableQuerySpec): Promise<Dataset> {
    return this.executeGraphQLQuery(() => getGroupDatasetQueryOptions(tableSelectionSpec));
  }

  public fetchDataSeries(datasetId: number, dataSeriesNames: string[]): Promise<DataSeries[]> {
    return this.executeGraphQLQuery(() => getDataSeriesQueryOptions(datasetId, dataSeriesNames));
  }

  public fetchCoordinateLists(coordinateListIds: number[]): Promise<CoordinateList[]> {
    return this.executeGraphQLQuery(() => getCoordinateListsQueryOptions(coordinateListIds));
  }

  public fetchCoordinateListsByGenome(genomeId: number): Promise<CoordinateList[]> {
    return this.executeGraphQLQuery(() => getCoordinateListsByGenomeQueryOptions(genomeId));
  }
  public searchDataSeries(
    datasetId: number,
    searchTerm: string,
    limit: number = DEFAULT_LIMIT_1000,
  ): Promise<DataSeries[]> {
    return this.executeGraphQLQuery(() => getDataSeriesSearchQueryOptions(datasetId, searchTerm, limit));
  }

  public fetchGroupDataSeries(
    dataSeriesNames: string[],
    tableSelectionSpec: InputTableQuerySpec,
  ): Promise<DataSeries[]> {
    return this.executeGraphQLQuery(() => getGroupDataSeriesQueryOptions(dataSeriesNames, tableSelectionSpec));
  }

  public searchGroupDataSeries(
    searchTerm: string,
    tableSelectionSpec: InputTableQuerySpec,
    limit: number = DEFAULT_LIMIT_1000,
  ): Promise<DataSeries[]> {
    return this.executeGraphQLQuery(() => getGroupDataSeriesSearchQueryOptions(searchTerm, limit, tableSelectionSpec));
  }

  public searchSequencedGenomes(
    searchTerm: string,
    excludeVirtualGenomes: boolean,
    limit: number = DEFAULT_LIMIT_1000,
  ): Promise<GenomeSearch> {
    return this.executeGraphQLQuery(() =>
      getSequencedGenomeSearchQueryOptions(searchTerm, excludeVirtualGenomes, limit),
    );
  }

  public searchGenomesForSamples(searchTerm: string, limit: number = DEFAULT_LIMIT_1000): Promise<GenomeSearch> {
    return this.executeGraphQLQuery(() => getSearchGenomesForSamplesQueryOptions(searchTerm, limit));
  }

  public searchGenomes(searchTerm: string, limit: number = DEFAULT_LIMIT_1000): Promise<GenomeSearch> {
    return this.executeGraphQLQuery(() => getGenomeSearchQueryOptions(searchTerm, limit));
  }

  public fetchAvailableDatasets(genomeId: number): Promise<Dataset[]> {
    return this.executeGraphQLQuery(() => getAvailableDatasetsQueryOptions(genomeId));
  }

  public fetchSummaryGenome(genomeId: number, contigLimit: number = DEFAULT_LIMIT_100): Promise<Organism> {
    return this.executeGraphQLQuery(() => getGenomeSummaryQueryOptions(genomeId, contigLimit));
  }

  public fetchGenomeRelatedProjects(genomeId: number): Promise<Organism> {
    return this.executeGraphQLQuery(() => getGenomeRelatedProjectsQueryOptions(genomeId));
  }

  public fetchAuditLogExportFilterOptions(): Promise<AuditFilterOption> {
    return this.executeGraphQLQuery(() => getFetchAuditLogExportFilterOptionsQueryOptions());
  }

  public fetchGenomeGffFeatureStatistics(genomeId: number): Promise<Organism> {
    return this.executeGraphQLQuery(() => getGenomeGffFeatureQueryOptions(genomeId));
  }

  public fetchTrackFeatures(
    featureType: string,
    contigId: number,
    range: Range,
    featureTypeId: number,
    withSequence: boolean,
  ): Promise<TrackFeatures> {
    if (featureType === TRACK_TYPE.NUFEATURE || featureType === TRACK_TYPE.AAFEATURE) {
      return this.executeGraphQLQuery(() =>
        getTrackNuAaFeaturesQueryOptions(featureType, featureTypeId, contigId, range),
      );
    } else if (featureType === TRACK_TYPE.MARKER_NUS) {
      return this.executeGraphQLQuery(() => getTrackMarkerQueryOptions(featureTypeId, contigId, range));
    }

    return this.executeGraphQLQuery(() => getTrackFeaturesQueryOptions(featureType, contigId, range, withSequence));
  }

  public fetchTrackExpressionValues(
    featureType: string,
    contigId: number,
    range: Range,
    _featureSubtypeId: number,
    datasetId: number,
  ): Promise<TrackFeatures> {
    return this.executeGraphQLQuery(() =>
      getTrackExpressionValuesQueryOptions(featureType, contigId, range, datasetId),
    );
  }

  public fetchCommunityAnnotationsIndicators(
    featureType: string,
    contigId: number,
    range: Range,
    featureSubtypeId: number,
  ): Promise<TrackGroupIndicator> {
    return this.executeGraphQLQuery(() =>
      getFetchTrackCommunityAnnotationsQueryOptions(featureType, contigId, range, featureSubtypeId),
    );
  }

  public fetchProfileFeatures(profileTypeId: number, contigId: number, range?: Range): Promise<TrackNuProfile> {
    return this.executeGraphQLQuery(() => getProfileFeaturesQueryOptions(profileTypeId, contigId, range));
  }

  public fetchNgsCoverageFeatures(
    bamFileId: number,
    contigId: number,
    range: Range,
    resolution: number,
  ): Promise<Coverage[]> {
    return this.executeGraphQLQuery(() => getNgsCoverageFeaturesQueryOptions(bamFileId, contigId, range, resolution));
  }

  public fetchNgsPileupFeatures(
    bamFileId: number,
    contigId: number,
    range: Range,
    includeSoftClippedReads: boolean,
    lanesCount = 10, // TODO settings
  ): Promise<PileupRow[]> {
    return this.executeGraphQLQuery(() =>
      getNgsPileupFeaturesQueryOptions(bamFileId, contigId, range, lanesCount, includeSoftClippedReads),
    );
  }

  public fetchNgsReadTooltip(
    bamFileId: number,
    elementStart: number,
    readName: string,
    region: InputRegionWithoutStrand,
  ): Promise<FullPileupAlignment> {
    return this.executeGraphQLQuery(() => getNgsReadTooltipQueryOptions(bamFileId, elementStart, readName, region));
  }

  public fetchBamFileSummary(bamFileId: number): Promise<BamSummary> {
    return this.executeGraphQLQuery(() => getBamFileSummaryQueryOptions(bamFileId));
  }

  public fetchBedFeatures(bedFileId: number, contigId: number, range: Range): Promise<TrackFeatures> {
    return this.executeGraphQLQuery(() => getBedFeaturesQueryOptions(bedFileId, contigId, range));
  }

  public fetchCoordinateFeatures(coordinateListId: number, contigId: number, range: Range): Promise<TrackFeatures> {
    return this.executeGraphQLQuery(() => getCoordinateFeaturesQueryOptions(coordinateListId, contigId, range));
  }

  public fetchTrackVariants(
    contigId: number,
    range: Range,
    refGenomeId: number,
    filterExpression: string,
    limit: number = DEFAULT_LIMIT_100,
    mutantGenomeIds: number[],
  ): Promise<TrackVariants> {
    return this.executeGraphQLQuery(() =>
      getTrackVariantsQueryOptions(contigId, range, refGenomeId, filterExpression, limit, mutantGenomeIds),
    );
  }

  public fetchTrackVcfVariants(
    contigId: number,
    range: Range,
    refGenomeId: number,
    filterExpression: string,
    limit: number = DEFAULT_LIMIT_100,
    mutantGenomeIds: number[],
  ): Promise<TrackVariants> {
    return this.executeGraphQLQuery(() =>
      getTrackVcfVariantsQueryOptions(contigId, range, refGenomeId, filterExpression, limit, mutantGenomeIds),
    );
  }

  public fetchProject(projectId: number): Promise<Project> {
    return this.executeGraphQLQuery(() => getProjectQueryOptions(projectId));
  }

  public fetchProjectActivity(activityId: number): Promise<ProjectActivity> {
    return this.executeGraphQLQuery(() => getProjectActivityQueryOptions(activityId));
  }

  public fetchProjectActivities(projectId: number): Promise<ProjectActivity[]> {
    return this.executeGraphQLQuery(() => getProjectActivitiesQueryOptions(projectId));
  }

  public fetchProjectList(sampleTypeId: number, includeStagedProjects = false): Promise<ProjectList> {
    return this.executeGraphQLQuery(() => getProjectListQueryOptions(sampleTypeId, includeStagedProjects));
  }

  public fetchDashboardList(): Promise<Dashboard[]> {
    return this.executeGraphQLQuery(() => getDashboardListQueryOptions());
  }

  public saveDashboard(dashboard: InputDashboardInput): Promise<Dashboard> {
    return this.executeGraphQLMutation(() => getSaveDashboardMutationOptions(dashboard));
  }

  public fetchDashboardCardProjectsList(config: IProjectsListCardConfig): Promise<Project[]> {
    return this.executeGraphQLQuery(() => getDashboardCardProjectsListQueryOptions(config));
  }

  public fetchDashboardCardRecentActivityList(config: IRecentActivitiesCardConfig): Promise<RecentItem[]> {
    return this.executeGraphQLQuery(() => getDashboardCardRecentActivitiesListQueryOptions(config));
  }

  public fetchProjectCapabilitiesList(): Promise<Capability[]> {
    return this.executeGraphQLQuery(() => getProjectCapabilitiesListQueryOptions());
  }

  public fetchProjectSequencedOrganisms(projectId: number): Promise<Organism[]> {
    return this.executeGraphQLQuery(() => getProjectSequencedOrganismsQueryOptions(projectId));
  }

  public isProjectNameDefined(projectName: string, ignoreId: number): Promise<boolean> {
    return this.executeGraphQLQuery(() => getValidateProjectNameQueryOptions(projectName, ignoreId));
  }

  public isUsergroupNameDefined(usergroupName: string, usergroupId: number): Promise<boolean> {
    return this.executeGraphQLQuery(() => getValidateUsergroupNameQueryOptions(usergroupName, usergroupId));
  }

  public fetchProtein(proteinId: number): Promise<Protein> {
    return this.executeGraphQLQuery(() => getProteinQueryOptions(proteinId));
  }

  public fetchGroup(groupId: number): Promise<Group> {
    return this.executeGraphQLQuery(() => getSavedGroupQueryOptions(groupId));
  }

  public fetchSavedItemsForAttachments(projectId: number, listing: SavedItemsListing[]): Promise<any> {
    return this.executeGraphQLQuery(() => getSavedItemsForAttachmentsQueryOptions(projectId, listing));
  }

  public fetchSavedItems(projectId: number, type: string, subType: string): Promise<SavedItem[]> {
    return this.executeGraphQLQuery(() => getSavedItemsQueryOptions(projectId, type, subType));
  }

  public fetchDashboardCardSavedItems(
    config: ISavedItemsCardConfig,
    activeSavedItemsListing: SavedItemsItemsArgs,
    filterTerm: string,
  ): Promise<SavedItem[]> {
    return this.executeGraphQLQuery(() =>
      getDashboardCardSavedItemsQueryOptions(config, activeSavedItemsListing, filterTerm),
    );
  }

  public fetchSavedItemsListing(projectId: number): Promise<SavedItemsListing[]> {
    return this.executeGraphQLQuery(() => getSavedItemsListingQueryOptions(projectId));
  }

  public fetchSearchResult(searchResultId: number): Promise<SearchResult> {
    return this.executeGraphQLQuery(() => getSearchResultQueryOptions(searchResultId));
  }

  public fetchTableData(queryParams: IDataTableQueryParams, columns?: ITableColumn[]): Promise<Table> {
    return this.executeGraphQLQuery(() => getDataTableQueryOptions(queryParams, columns));
  }

  public getGenomeIDsForVariants(tableSpec: ITableSpecWithContext): Promise<number[]> {
    return this.executeGraphQLQuery(() => getVariantRelatedGenomes(tableSpec));
  }

  public fetchTranscript(transcriptId: number): Promise<Transcript> {
    return this.executeGraphQLQuery(() => getTranscriptQueryOptions(transcriptId));
  }

  public fetchContig(contigId: number): Promise<Contig> {
    return this.executeGraphQLQuery(() => getContigSummaryQueryOptions(contigId));
  }

  public fetchContigForGenomeBrowser(contigId: number): Promise<Contig> {
    return this.executeGraphQLQuery(() => getContigForGenomeBrowserQueryOptions(contigId));
  }

  public fetchSample(sampleId: number): Promise<Sample> {
    return this.executeGraphQLQuery(() => getSampleQueryOptions(sampleId));
  }

  public fetchSampleValues(sampleId: number): Promise<AttributeValue[][]> {
    return this.executeGraphQLQuery(() => getSampleValuesQueryOptions(sampleId));
  }

  public fetchSampleParent(parentId: number): Promise<Parent> {
    return this.executeGraphQLQuery(() => getSampleParentQueryOptions(parentId));
  }

  public fetchSampleParentStatistics(parentIds: number[]): Promise<ParentStatistic[]> {
    return this.executeGraphQLQuery(() => getSampleParentStatisticsQueryOptions(parentIds));
  }

  public fetchSampleTypes(): Promise<SampleType[]> {
    return this.executeGraphQLQuery(() => getSampleTypesQueryOptions());
  }

  public fetchAdminSampleTypes(): Promise<SampleType[]> {
    return this.executeGraphQLQuery(() => getAdminSampleTypesQueryOptions());
  }

  public fetchAdminSampleStatusSets(): Promise<SampleStatus[]> {
    return this.executeGraphQLQuery(() => getAdminSampleStatusSetsQueryOptions());
  }

  public fetchAdminParentTypes(): Promise<ParentType[]> {
    return this.executeGraphQLQuery(() => getAdminParentTypesQueryOptions());
  }

  public fetchSampleIdsForParentIds(parentIds: number[]): Promise<number[]> {
    return this.executeGraphQLQuery(() => getFetchSampleIdsForParentIdOptions(parentIds));
  }

  public fetchMinimalParentTypes(): Promise<ParentType[]> {
    return this.executeGraphQLQuery(() => getMinimalParentTypesQueryOptions());
  }

  public fetchParentTypesForSampleTypeCreation(): Promise<ParentType[]> {
    return this.executeGraphQLQuery(() => getParentTypesForSampleTypeCreationQueryOptions());
  }

  public fetchAdminParentType(parentTypeId: number): Promise<ParentType> {
    return this.executeGraphQLQuery(() => getAdminParentTypeQueryOptions(parentTypeId));
  }

  public fetchAdminSampleType(sampleTypeId: number): Promise<SampleType> {
    return this.executeGraphQLQuery(() => getAdminSampleTypeQueryOptions(sampleTypeId));
  }

  public validateSampleTypeExists(name: string): Promise<boolean> {
    return this.executeGraphQLQuery(() => getSampleTypeExistsQueryOptions(name));
  }

  public validatePlaybookExists(name: string, suffix: string): Promise<boolean> {
    return this.executeGraphQLQuery(() => getPlaybookExistsQueryOptions(name, suffix));
  }

  public validateParentTypeExists(name: string): Promise<boolean> {
    return this.executeGraphQLQuery(() => getParentTypeExistsQueryOptions(name));
  }

  public validateIsAcceptableNewOracleAccountNameQueryOptions(username: string): Promise<boolean> {
    return this.executeGraphQLQuery(() => getIsAcceptableNewOracleAccountNameQueryOptions(username));
  }

  public checkParentNameUniqueness(name: string, parentId: number): Promise<boolean> {
    return this.executeGraphQLQuery(() => getIsParentNameUniqueQueryOptions(name, parentId));
  }

  public fetchSampleAttributeDefaults(sampleTypeId: number): Promise<AttributeDefaultValueMapEntry[]> {
    return this.executeGraphQLQuery(() => getSampleAttributeDefaultsQueryOptions(sampleTypeId));
  }

  public fetchRawSampleAttributeDefaults(sampleTypeId: number): Promise<RawAttributeDefaultMapEntry[]> {
    return this.executeGraphQLQuery(() => getRawSampleAttributeDefaultsQueryOptions(sampleTypeId));
  }

  public fetchAttributes(): Promise<Attribute[]> & ICanCancel {
    return this.executeGraphQLQueryWithCancel(() => getAttributesQueryOptions());
  }

  public fetchAttributesAsDecoration(): Promise<Attribute[]> & ICanCancel {
    return this.executeGraphQLQueryWithCancel(() => getAttributesAsDecorationQueryOptions());
  }

  public fetchAttribute(attributeId: number): Promise<Attribute> {
    return this.executeGraphQLQuery(() => getAttributeQueryOptions(attributeId));
  }

  public fetchSampleStatuses(statusSetId: number): Promise<SampleStatus[]> {
    return this.executeGraphQLQuery(() => getSampleStatusesQueryOptions(statusSetId));
  }

  public fetchSequence(sequenceId: number): Promise<Sequence> {
    return this.executeGraphQLQuery(() => getSequenceQueryOptions(sequenceId));
  }

  public fetchNotificationList(readNotificationsLimit: number): Promise<Notification[]> {
    return this.executeGraphQLQuery(() => getNotificationListQueryOptions(readNotificationsLimit));
  }

  public fetchNotification(notificationId: number): Promise<Notification> {
    return this.executeGraphQLQuery(() => getNotificationQueryOptions(notificationId));
  }

  public fetchGenMutation(mutationId: number, refGenomeId: number): Promise<GenMutation> {
    return this.executeGraphQLQuery(() => getGenMutationQueryOptions(mutationId, refGenomeId));
  }

  public fetchView(viewId: number): Promise<View> {
    return this.executeGraphQLQuery(() => getSavedViewQueryOptions(viewId));
  }

  public fetchContigSearchResults(genomeId: number, searchTerm: string, limit: number): Promise<ContigSearch> {
    return this.executeGraphQLQuery(() => getContigSearchResultsQueryOptions(genomeId, searchTerm, limit));
  }

  public fetchTableDataRowPosition(
    targetId: number,
    targetType: string,
    tableQuerySpec: InputTableQuerySpec,
  ): Promise<RowIndex> {
    return this.executeGraphQLQuery(() => getTableDataRowPositionQueryOptions(targetId, targetType, tableQuerySpec));
  }

  public fetchGridColumn(columnId: number, tableQuerySpec: InputTableQuerySpec): Promise<ITableColumn> {
    return this.executeGraphQLQuery(() => getColumnQueryOptions(columnId, tableQuerySpec));
  }

  public fetchBlastIndexList(searchTerm: string, limit = 200): Promise<BlastIndex[]> {
    return this.executeGraphQLQuery(() => getBlastIndexListQueryOptions(searchTerm, limit));
  }

  public fetchJob(jobId: number, bestHit = true): Promise<BlastJob> {
    return this.executeGraphQLQuery(() => getJobQueryOptions(jobId, bestHit));
  }

  public fetchJobForRerun(jobId: number): Promise<BlastJob> {
    return this.executeGraphQLQuery(() => getJobForRerunQueryOptions(jobId));
  }

  public fetchJobResults(jobId: number, queryIds: number[], bestHit = false): Promise<JobResult[]> {
    return this.executeGraphQLQuery(() => getJobResultsQueryOptions(jobId, queryIds, bestHit));
  }

  public validateBlastInputSequence(jobValidation: InputJobValidation): Promise<ValidationError[]> {
    return this.executeGraphQLQuery(() => getValidateBlastInputSequenceQueryOptions(jobValidation));
  }

  public validateSequenceUpload(
    sequenceUploadValidationInput: Partial<InputSequenceUploadValidation>,
  ): Promise<ValidationError[]> {
    return this.executeGraphQLQuery(() => getValidateSequenceUploadQueryOptions(sequenceUploadValidationInput));
  }

  public fetchSequenceIdentifiers(sequenceIdentifiersInput: InputSequenceIdentifiersInput): Promise<string[]> {
    return this.executeGraphQLQuery(() => getSequenceIdentifiersQueryOptions(sequenceIdentifiersInput));
  }

  public fetchContentBrowserCounts(): Promise<ContentBrowser> {
    return this.executeGraphQLQuery(() => getContentBrowserCountsQueryOptions());
  }

  public fetchUserGroupAdminCounts(projectGroupAdminKeys: string[]): Promise<AdminBrowser> {
    return this.executeGraphQLQuery(() => getUserGroupAdminCountsQueryOptions(projectGroupAdminKeys));
  }

  public fetchUserGroups(): Promise<UserGroup[]> {
    return this.executeGraphQLQuery(() => getFetchUserGroupsQueryOptions());
  }

  public fetchBasicUserGroups(): Promise<UserGroup[]> {
    return this.executeGraphQLQuery(() => getFetchBasicUserGroups());
  }

  public fetchAccessibleUserGroups(): Promise<UserGroup[]> {
    return this.executeGraphQLQuery(() => getAccessibleUserGroups());
  }

  public fetchContentBrowserListing(featureType: string, groupByAttribute: string): Promise<ContentBrowser> {
    return this.executeGraphQLQuery(() => getContentBrowserListingQueryOptions(featureType, groupByAttribute));
  }

  public fetchAdminBrowserListing(featureType: string, groupByAttribute: string): Promise<AdminBrowser> {
    return this.executeGraphQLQuery(() => getAdminBrowserListingQueryOptions(featureType, groupByAttribute));
  }

  public fetchContentBrowserInfo(
    featureType: string,
    groupByAttribute: string,
    filterKey: string,
  ): Promise<ContentBrowser> {
    return this.executeGraphQLQuery(() => getContentBrowserInfoQueryOptions(featureType, groupByAttribute, filterKey));
  }

  public fetchAdminBrowserInfo(
    featureType: string,
    groupByAttribute: string,
    filterKey: string,
  ): Promise<AdminBrowser> {
    return this.executeGraphQLQuery(() => getAdminBrowserInfoQueryOptions(featureType, groupByAttribute, filterKey));
  }

  public fetchAdvancedSearchTypes(): Promise<SearchType[]> {
    return this.executeGraphQLQuery(() => getAdvancedSearchTypesQueryOptions());
  }

  public fetchAdvancedSearchAttributes(entityType: string, subTypeId: number): Promise<SearchAttribute[]> {
    return this.executeGraphQLQuery(() => getAdvancedSearchAttributesQueryOptions(entityType, subTypeId));
  }

  public fetchAdvancedSearchPreview(advancedSearchInput: InputAdvancedSearchInput): Promise<AdvancedSearchPreview> {
    return this.executeGraphQLQuery(() => getAdvancedSearchPreviewQueryOptions(advancedSearchInput));
  }

  public fetchSearchScopeOptions(
    searchTerm: string,
    restrictionType: string,
    limit: number = DEFAULT_LIMIT_1000,
  ): Promise<SearchScope[]> {
    return this.executeGraphQLQuery(() => getSearchScopeOptions(searchTerm, restrictionType, limit));
  }

  public searchProjectFiles(searchTerms: string[], filterExpression: string): Promise<ProjectFileSearch[]> {
    return this.executeGraphQLQuery(() => getSearchProjectFilesOptions(searchTerms, filterExpression));
  }

  public fileNameExists(projectId: number, resourceNames: string[]): Promise<ProjectFile[]> {
    return this.executeGraphQLQuery(() => getFileNameExistsOptions(projectId, resourceNames));
  }

  public getOrganismsFromProjectFileById(fileIds: number[]): Promise<ProjectFile[]> {
    return this.executeGraphQLQuery(() => getOrganismsFromProjectFileByIdOptions(fileIds));
  }

  public getGenomeViewerFilesById(fileIds: number[]): Promise<ProjectFile[]> {
    return this.executeGraphQLQuery(() => getGenomeViewerFilesByIdOption(fileIds));
  }

  public getProjectFilesDataForSendingToGenomeViewer(fileIds: number[]): ISyncResponse<ProjectFile[]> {
    return this.executeGraphQLQuerySync(() => getProjectFilesDataForSendingToGenomeViewerOptions(fileIds));
  }

  public validateBamFileAgainstRefGenomes(bamFileId: number, genomeId: number): Promise<string> {
    return this.executeGraphQLQuery(() => getValidateBamFileAgainstRefGenomesOptions(bamFileId, genomeId));
  }

  public validateBedFileAgainstRefGenomes(bedFileId: number, genomeId: number): Promise<string> {
    return this.executeGraphQLQuery(() => getValidateBedFileAgainstRefGenomesOptions(bedFileId, genomeId));
  }

  public validateSingleGroupAgainstProjectUserGroup(
    projectId: number,
    type: string,
    selectionSpec: InputTableQuerySpec,
    targetType: string,
  ): Promise<UserGroupItemValidationResult> {
    return this.executeGraphQLQuery(() =>
      getValidateSingleGroupAgainstProjectUserGroupOptions(projectId, type, selectionSpec, targetType),
    );
  }

  public validateSavedGroupsAgainstProjectUserGroup(projectId: number, groupIds: number[]): Promise<boolean> {
    return this.executeGraphQLQuery(() => getValidateSavedGroupsAgainstProjectUserGroupOptions(projectId, groupIds));
  }

  public validateSingleViewAgainstProjectUserGroup(viewInputParams: InputViewInput): Promise<boolean> {
    return this.executeGraphQLQuery(() => getValidateSingleViewAgainstProjectUserGroupOptions(viewInputParams));
  }

  public validateSavedViewsAgainstProjectUserGroup(projectId: number, viewIds: number[]): Promise<boolean> {
    return this.executeGraphQLQuery(() => getValidateSavedViewsAgainstProjectUserGroupOptions(projectId, viewIds));
  }

  public validateFlagsAgainstProjectUserGroup(projectId: number, flagIds: number[]): Promise<boolean> {
    return this.executeGraphQLQuery(() => getValidateFlagsAgainstProjectUserGroupOptions(projectId, flagIds));
  }

  public fetchSampleTypeWizardData(sampleTypeId: number, excludeAnalyticalAttributes?: boolean): Promise<SampleType> {
    return this.executeGraphQLQuery(() => getSampleTypeWizardQueryOptions(sampleTypeId, excludeAnalyticalAttributes));
  }

  public fetchSampleEditingColumns(inputSpec: InputTableQuerySpec): Promise<EditSamplesGrid> {
    return this.executeGraphQLQuery(() => getSampleEditingColumnQueryOptions(inputSpec));
  }

  public fetchSampleEditingData(inputSpec: InputTableQuerySpec, attributeIds: number[]): Promise<EditSamplesGrid> {
    return this.executeGraphQLQuery(() => getSampleEditingDataQueryOptions(inputSpec, attributeIds));
  }

  public fetchSampleTypeForPlaybooksButton(inputSpec: InputTableQuerySpec): Promise<EditSamplesGrid> {
    return this.executeGraphQLQuery(() => getSampleTypeForPlaybooksButton(inputSpec));
  }

  public checkAttributeUniqueness(samplesValidationInputData: InputSamplesValidationInputData): Promise<string[]> {
    return this.executeGraphQLQuery(() => getCheckAttributeUniquenessQueryOptions(samplesValidationInputData));
  }

  public loadColumnTemplate(columnTemplate: Template, tableSpec: InputTableQuerySpec): Promise<Template> {
    return this.executeGraphQLQuery(() => getLoadColumnTemplateQueryOptions(columnTemplate, tableSpec));
  }

  public fetchAllCapabilities(): Promise<Capability[]> {
    return this.executeGraphQLQuery(() => getLoadAllCapabilitiesQueryOptions());
  }

  public fetchAllRoles(): Promise<Role[]> {
    return this.executeGraphQLQuery(() => getLoadAllRolesQueryOptions());
  }

  public resolvePlaceholderNextElement(
    sampleTypeId: number,
    values: InputAttributeValueInput[],
    sequence: string[],
    loopOverSequence = false,
  ): Promise<string> {
    return this.executeGraphQLQuery(() => resolveTemplateNextElement(sampleTypeId, values, sequence, loopOverSequence));
  }

  public resolvePlaceholderNext(sampleTypeId: number, values: InputAttributeValueInput[]): Promise<number> {
    return this.executeGraphQLQuery(() => resolveTemplateNext(sampleTypeId, values));
  }

  public resolvePlaceholderCount(sampleTypeId: number, values: InputAttributeValueInput[]): Promise<number> {
    return this.executeGraphQLQuery(() => resolveTemplateCount(sampleTypeId, values));
  }

  public fetchNumberOfFreeSeats(): Promise<number> {
    return this.executeGraphQLQuery(() => getNumberOfFreeSeatsQueryOptions());
  }

  public fetchConfiguration(): Promise<Configuration> {
    return this.executeGraphQLQuery(() => getConfigurationQueryOptions());
  }

  public translateAnalyzerIds(data: InputAnalyzerIdMappingInput): Promise<AnalyzerIdMapping[]> {
    return this.executeGraphQLQuery(() => getTranslateAnalyzerIdsOptions(data));
  }

  public fetchNCBIGenomesAssemblies(ids: number[]): Promise<GenomeAssembly[]> {
    return this.executeGraphQLQuery(() => getGenomeAssemblies(ids));
  }

  public fetchAdminTools(): Promise<Tool[]> {
    return this.executeGraphQLQuery(() => getAllAdminTools());
  }

  public fetchAllTaxonomies(): Promise<Taxonomy[]> {
    return this.executeGraphQLQuery(() => getAllTaxonomies());
  }

  public hasInternetConnection(url: string | null): Promise<boolean> {
    return this.executeGraphQLQuery(() => hasInternetConnectionOptions(url));
  }

  public isValidGenomeName(name: string): Promise<boolean> {
    return this.executeGraphQLQuery(() => isValidGenomeName(name));
  }

  public isValidGenomeIdentifier(identifier: string): Promise<boolean> {
    return this.executeGraphQLQuery(() => isValidGenomeIdentifier(identifier));
  }

  public fetchTestGenomicGeneTable(tableSpec: InputTableQuerySpec): Promise<Table> {
    return this.executeGraphQLQuery(() => getFetchTestGenomicGeneOptions(tableSpec));
  }

  public fetchSystemDetails(systemId: string): Promise<ServerStatusInfo> {
    return this.executeGraphQLQuery(() => getFetchSystemDetailsOptions(systemId));
  }

  public fetchSampleTypeStatuses(config: ISampleStatusesCardConfig): Promise<SampleCount> {
    return this.executeGraphQLQuery(() => getFetchSampleTypeStatusOptions(config));
  }

  public fetchPlaybooks(): Promise<Playbook[]> {
    return this.executeGraphQLQuery(() => getFetchPlaybooksOptions());
  }

  public fetchAdminPlaybooks(): Promise<Playbook[]> {
    return this.executeGraphQLQuery(() => getFetchAdminPlaybooksOptions());
  }

  public fetchAdminPlaybookCategories(): Promise<PlaybookCategory[]> {
    return this.executeGraphQLQuery(() => getFetchPlaybookCategories());
  }

  public fetchAdminPlaybook(playbookId: number): Promise<Playbook> {
    return this.executeGraphQLQuery(() => getFetchAdminPlaybookOptions(playbookId));
  }

  public fetchPlaybook(playbookId: number): Promise<Playbook> {
    return this.executeGraphQLQuery(() => getFetchPlaybookOptions(playbookId));
  }

  public fetchNfPlaybook(playbookId: number): Promise<Playbook> {
    return this.executeGraphQLQuery(() => getFetchNfPlaybookOptions(playbookId));
  }

  public fetchApprovedWorkflows(workflowID: string, playbookTypeId: number): Promise<Workflow[]> {
    return this.executeGraphQLQuery(() => getFetchApprovedWorkflowsOptions(workflowID, playbookTypeId));
  }

  public fetchNfWorkflows(): Promise<NfWorkflow[]> {
    return this.executeGraphQLQuery(() => getFetchNfWorkflowsOptions());
  }

  public fetchPublishedWorkflows(): Promise<WorkflowP2[]> {
    return this.executeGraphQLQuery(() => getFetchPublishedWorkflowsOptions());
  }

  public fetchNfWorkflow(workflowName: string): Promise<NfWorkflow> {
    return this.executeGraphQLQuery(() => getFetchNfWorkflowOptions(workflowName));
  }

  public fetchP2Workflow(workflowId: number): Promise<WorkflowP2> {
    return this.executeGraphQLQuery(() => getFetchP2WorkflowOptions(workflowId));
  }

  public loadP2WorkflowForParamsValidation(workflowId: number): Promise<WorkflowP2> {
    return this.executeGraphQLQuery(() => getLoadP2WorkflowForParamsValidationOptions(workflowId));
  }

  public fetchProjectFilesForAttachments(projectId: number): Promise<ProjectFile[]> {
    return this.executeGraphQLQuery(() => fetchProjectFilesForAttachmentsQueryOptions(projectId));
  }

  public fileNameExistsInCollector(folderId: number, fileNames: string[]): Promise<string[]> {
    return this.executeGraphQLQuery(() => getFileNameExistsInCollector(folderId, fileNames));
  }

  public fetchAllOrgPropertyTypes(): Promise<OrganismPropertyType[]> {
    return this.executeGraphQLQuery(() => getfetchAllOrgPropertyTypesQueryOptions());
  }

  public fetchOrgPropertyType(orgPropertyTypeId: number): Promise<OrganismPropertyType> {
    return this.executeGraphQLQuery(() => getfetchOrgPropertyTypeQueryOptions(orgPropertyTypeId));
  }

  public fetchApplicableOrgPropertyTypes(orgIds: number[]): Promise<GenomicFeature[]> {
    return this.executeGraphQLQuery(() => getfetchApplicableOrgPropertyTypesQueryOptions(orgIds));
  }

  public fetchPhenotypeValues(
    orgIds: number[],
    phenotypeIds: number[],
    attributeIds: number[],
  ): Promise<OrganismPhenotype[]> {
    return this.executeGraphQLQuery(() => getFetchPhenotypeValuesQueryOptions(orgIds, phenotypeIds, attributeIds));
  }

  public fetchSequenceFeatureTypes(): Promise<SequenceFeatureTypes> {
    return this.executeGraphQLQuery(() => getFetchSequenceFeatureTypesQueryOptions());
  }

  public fetchSequenceTypes(): Promise<SequenceType[]> {
    return this.executeGraphQLQuery(() => getFetchSequenceTypesQueryOptions());
  }

  public fetchValuesForOrganismPropertyType(
    orgPropertyTypeId: number,
    searchTerm: string,
  ): Promise<OrganismProperty[]> {
    return this.executeGraphQLQuery(() =>
      getFetchValuesForOrganismPropertyTypeQueryOptions(orgPropertyTypeId, searchTerm),
    );
  }

  public fetchMarker(markerId: number): Promise<Marker> {
    // MarkerSummary
    return this.executeGraphQLQuery<Marker>(() => getMarkerQueryOptions(markerId));
  }

  public fetchNuFeature(nuFeatureId: number): Promise<NuFeature> {
    // NuFeatureSummary
    return this.executeGraphQLQuery<NuFeature>(() => getNuFeatureQueryOptions(nuFeatureId));
  }

  public fetchAaFeature(aaFeatureId: number): Promise<AaFeature> {
    // AAFeatureSummary
    return this.executeGraphQLQuery<AaFeature>(() => getAaFeatureQueryOptions(aaFeatureId));
  }

  public fetchEvent(eventId: number): Promise<PlaybookEvent> {
    return this.executeGraphQLQuery<PlaybookEvent>(() => getEventQueryOptions(eventId));
  }

  public fetchEventForRerun(eventId: number): Promise<PlaybookEvent> {
    return this.executeGraphQLQuery<PlaybookEvent>(() => getEventForRerunQueryOptions(eventId));
  }

  public fetchEvents(limit: number, categoryId?: number, start = 0, currentUserOnly = false): Promise<PlaybookEvent[]> {
    return this.executeGraphQLQuery<PlaybookEvent[]>(() =>
      getRecentEventsQueryOptions(limit, categoryId, start, currentUserOnly),
    );
  }

  public fetchPlaybookCategoryById(categoryId: number): Promise<PlaybookCategory> {
    return this.executeGraphQLQuery(() => getFetchPlaybookCategoryById(categoryId));
  }

  public getSequenceGroupTypes(tableQuerySpec: InputTableQuerySpec): Promise<SequenceGroupTypeCount[]> {
    return this.executeGraphQLQuery(() => getSequenceGroupTypesQueryOptions(tableQuerySpec));
  }

  public getMutantGenomesByAnalysisGroup(
    analysisGroup: VariantAnalysisGroup,
    mutantGenomesIds: number[],
  ): Promise<Organism[]> {
    return this.executeGraphQLQuery(() => getMutantGenomesByAnalysisGroupQueryOptions(analysisGroup, mutantGenomesIds));
  }

  public getRelatedAnalysisGroups(organismId: number): Promise<VariantAnalysisGroup[]> {
    return this.executeGraphQLQuery(() => getRelatedAnalysisGroupsQueryOptions(organismId));
  }

  public checkVirtualGenomeExists(genomeIds: number[]): Promise<Organism[]> {
    return this.executeGraphQLQuery(() => getCheckVirtualGenomeExistsOptions(genomeIds));
  }

  /**
   ******************************   MUTATIONS ******************************
   */
  public submitJob(jobInput: InputJobInput): Promise<BlastJob> {
    return this.executeGraphQLMutation(() => getSubmitJobMutationOptions(jobInput));
  }

  public submitSequencesUpload(sequenceUploadInput: InputSequenceUploadInput): Promise<boolean> {
    return this.executeGraphQLMutation(() => getSubmitSequencesUploadMutationOptions(sequenceUploadInput));
  }

  public submitCoordinatesListUpload(coordListUploadInput: InputCoordinateListInput): Promise<boolean> {
    return this.executeGraphQLMutation(() => getSubmitCoordinatesListUploadMutationOptions(coordListUploadInput));
  }

  public saveUserParams(userParams: InputUserParamsInput): Promise<UserParams> {
    return this.executeGraphQLMutation(() => getUpdateUserParamsMutationOptions(userParams));
  }

  public saveSearchResult(searchTerm: string, types: InputSearchEntityTypeInput[]): Promise<SearchResult> {
    return this.executeGraphQLMutation(() => getCreateSearchResultMutationOptions(searchTerm, types));
  }

  public saveAdvancedSearchResult(advancedSearchInput: InputAdvancedSearchInput): Promise<SearchResult> {
    return this.executeGraphQLMutation(() => getCreateAdvancedSearchResultMutationOptions(advancedSearchInput));
  }

  public deleteProjectAndCascade(projectId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteProjectQueryOptions(projectId));
  }

  public saveProjectComment(
    projectId: number,
    comment: string,
    groupIds: number[],
    viewIds: number[],
    flagIds: number[],
    projectFileIds: number[],
  ): Promise<ProjectActivity> {
    return this.executeGraphQLMutation(() =>
      getCreateProjectCommentQueryOptions(projectId, comment, groupIds, viewIds, flagIds, projectFileIds),
    );
  }

  public saveProject(params: ICreateProjectParams): Promise<Project> {
    return this.executeGraphQLMutation(() => getCreateProjectMutationOptions(params));
  }

  public saveUsergroup(params: InputUserGroupInput): Promise<UserGroup> {
    return this.executeGraphQLMutation(() => getCreateUsergroupMutationOptions(params));
  }

  public modifyProject(params: InputModifyProjectInput): Promise<Project> {
    return this.executeGraphQLMutation(() => getModifyProjectMutationOptions(params));
  }

  public saveSelectionAsGroup(params: InputCreateGroupInput): Promise<Group> {
    return this.executeGraphQLMutation(() => getCreateGroupMutationOptions(params));
  }

  public saveAdminPlaybookCategory(playbookCategory: InputPlaybookCategoryInput): Promise<PlaybookCategory> {
    return this.executeGraphQLMutation(() => getSaveNewPlaybookCategory(playbookCategory));
  }

  public modifyAdminPlaybookCategory(playbookCategory: InputPlaybookCategoryInput): Promise<PlaybookCategory> {
    return this.executeGraphQLMutation(() => getModifyPlaybookCategory(playbookCategory));
  }

  public modifyPlaybookEvent(playbookEvent: InputModifyPlaybookEventInput): Promise<PlaybookEvent> {
    return this.executeGraphQLMutation(() => getModifyPlaybookEvent(playbookEvent));
  }

  public deleteAdminPlaybookCategory(playbookCategories: number[]): Promise<PlaybookCategory[]> {
    return this.executeGraphQLMutation(() => getDeletePlaybookCategory(playbookCategories));
  }

  public deleteGroupList(ids: number[]): Promise<Group[]> {
    return this.executeGraphQLMutation(() => getDeleteGroupsQueryOptions(ids));
  }

  public deleteParentList(ids: number[]): Promise<Parent[]> {
    return this.executeGraphQLMutation(() => getDeleteParentsQueryOptions(ids));
  }

  public deleteViewList(ids: number[]): Promise<View[]> {
    return this.executeGraphQLMutation(() => getDeleteViewsQueryOptions(ids));
  }

  public deleteFlagList(ids: number[]): Promise<RegionFlag[]> {
    return this.executeGraphQLMutation(() => getDeleteFlagsQueryOptions(ids));
  }
  public deleteCoordinateListList(ids: number[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteCoordinateListsQueryOptions(ids));
  }

  public createProjectHyperlink(
    name: string,
    url: string,
    folderId: number,
    description?: string,
  ): Promise<ProjectFile> {
    return this.executeGraphQLMutation(() => getCreateProjectHyperlink(name, url, folderId, description));
  }

  public createProjectFolder(name: string, folderId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getCreateProjectFolderQueryOptions(name, folderId));
  }

  public deleteActivityList(ids: number[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteActivitiesQueryOptions(ids));
  }

  public saveView(params: InputViewInput): Promise<View> {
    return this.executeGraphQLMutation(() => getCreateViewMutationOptions(params));
  }

  public updateProjectRole(input: InputUpdateProjectMemberRoleInput): Promise<any> {
    return this.executeGraphQLMutation(() => getUpdateProjectRoleMutation(input));
  }

  public addProjectMembers(input: InputAddProjectMemberInput): Promise<ProjectMember[]> {
    return this.executeGraphQLMutation(() => getAddProjectMemberMutation(input));
  }

  public removeProjectMember(input: InputRemoveProjectMemberInput): Promise<any> {
    return this.executeGraphQLMutation(() => getRemoveProjectMemberMutation(input));
  }

  public setNotificationAsRead(notificationId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => setNotificationAsRead(notificationId));
  }

  public setAllNotificationsAsRead(): Promise<boolean> {
    return this.executeGraphQLMutation(() => setAllNotificationsAsRead());
  }

  public performSetComparison(payload: InputSetComparisonInput): Promise<SetComparison> {
    return this.executeGraphQLQuery(() => getSetComparison(payload));
  }

  public createGroupFromSetComparison(payload: InputSetComparisonGroupInput): Promise<number> {
    return this.executeGraphQLMutation(() => getCreateGroupFromSetComparison(payload));
  }

  public copyGroups(groupIds: number[], projectId: number, isPrivate: boolean): Promise<boolean> {
    return this.executeGraphQLMutation(() => getCopyGroups(groupIds, projectId, isPrivate));
  }

  public copyViews(viewsIds: number[], projectId: number, isPrivate: boolean): Promise<boolean> {
    return this.executeGraphQLMutation(() => getCopyViews(viewsIds, projectId, isPrivate));
  }

  public copyFlags(flagIds: number[], projectId: number, isPrivate: boolean): Promise<boolean> {
    return this.executeGraphQLMutation(() => getCopyFlags(flagIds, projectId, isPrivate));
  }

  public copyFiles(fileIds: number[], targetId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getCopyFilesOptions(fileIds, targetId));
  }

  public moveFiles(fileIds: number[], targetId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getMoveFilesOptions(fileIds, targetId));
  }

  public groupRelated(payload: MutationGroupByTypeArgs): Promise<Group> {
    return this.executeGraphQLMutation(() => getGroupRelated(payload));
  }

  public createGroupInClassicPortal(input: MutationGroupInLegacyPortalArgs): Promise<IClassicGroup> {
    return this.executeGraphQLMutation(() => getCreateGroupInClassicPortal(input));
  }

  public logoutFromExternal(_redirect: string) {
    const url = `${window.origin + this.urls.basePath}home`;
    location.replace(`${this.urls.api}/logout?url=${url}`);
  }

  public logout(): Promise<boolean> {
    return this.executeGraphQLMutation(() => getLogoutMutationOptions());
  }

  public updateSavedItem(data: InputModifySavedItemInput): Promise<SavedItem> {
    return this.executeGraphQLMutation(() => getUpdateSavedItemOptions(data));
  }

  public modifyUserGroup(userGroupInput: InputUserGroupInput): Promise<UserGroup> {
    return this.executeGraphQLMutation(() => getModifyUserGroupQueryOptions(userGroupInput));
  }

  public deleteUserGroup(id: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteUserGroupQueryOptions(id));
  }

  public createGroupsUnion(groupIds: number[]): Promise<number> {
    return this.executeGraphQLMutation(() => getCreateGroupsUnionQueryOptions(groupIds));
  }

  public createSampleGroupFromParentsUnion(parentIds: number[]): Promise<number> {
    return this.executeGraphQLMutation(() => getCreateSampleGroupFromParentsUnionQueryOptions(parentIds));
  }

  public createTempGroup(selectionSpec: InputTableQuerySpec): Promise<number> {
    return this.executeGraphQLMutation(() => getCreateTempGroupQueryOptions(selectionSpec));
  }

  public runPlaybook(
    registrationInput: InputParentInput,
    playbookEventInput: InputPlaybookEventInput,
    workflowParamInputValues: InputWorkflowParamInput[],
    editingInput: InputEditSamplesInput,
  ): Promise<Parent> {
    return this.executeGraphQLMutation(() =>
      getRunPlaybookQueryOptions(registrationInput, playbookEventInput, workflowParamInputValues, editingInput),
    );
  }

  public saveEditedSamples(editSamplesInput: InputEditSamplesInput): Promise<Group> {
    return this.executeGraphQLMutation(() => getSaveEditedSampleQueryOptions(editSamplesInput));
  }

  public updateActiveTemplate(orderedColumns: InputColumnInput[], tableSpec: InputTableQuerySpec): Promise<boolean> {
    return this.executeGraphQLMutation(() => getUpdateActiveTemplateQueryOptions(orderedColumns, tableSpec));
  }

  public saveColumnTemplate(
    name: string,
    tableSpec: InputTableQuerySpec,
    columns: InputColumnInput[],
  ): Promise<Template> {
    return this.executeGraphQLMutation(() => getSaveColumnTemplateQueryOptions(name, tableSpec, columns));
  }

  public updateColumnTemplate(
    templateId: number,
    columns: InputColumnInput[],
    tableSpec: InputTableQuerySpec,
  ): Promise<Template> {
    return this.executeGraphQLMutation(() => getUpdateColumnTemplateQueryOptions(templateId, columns, tableSpec));
  }

  public deleteColumnTemplate(templateId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteColumnTemplateQueryOptions(templateId));
  }

  public updateRole(payload: InputRoleInput): Promise<Role> {
    return this.executeGraphQLMutation(() => getUpdateRoleQueryOptions(payload));
  }

  public createRole(payload: InputRoleInput): Promise<Role> {
    return this.executeGraphQLMutation(() => getCreateRoleQueryOptions(payload));
  }

  public saveNewSampleType(config: InputSampleTypeInput): Promise<SampleType> {
    return this.executeGraphQLMutation(() => getSaveSampleTypeQueryOptions(config));
  }

  public modifySampleType(config: InputSampleTypeInput): Promise<SampleType> {
    return this.executeGraphQLMutation(() => getModifySampleTypeQueryOptions(config));
  }

  public saveNewParentType(config: InputParentTypeInput): Promise<ParentType> {
    return this.executeGraphQLMutation(() => getSaveParentTypeQueryOptions(config));
  }

  public modifyParentType(config: InputParentTypeInput): Promise<ParentType> {
    return this.executeGraphQLMutation(() => getModifyParentTypeQueryOptions(config));
  }

  public createAttribute(payload: InputAttributeInput): Promise<Attribute> {
    return this.executeGraphQLMutation(() => getCreateAttributeQueryOptions(payload));
  }

  public modifyAttribute(payload: InputAttributeInput): Promise<Attribute> {
    return this.executeGraphQLMutation(() => getModifyAttributeQueryOptions(payload));
  }

  public deleteAttributes(payload: number[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteAttributesQueryOptions(payload));
  }

  public deleteSampleTypes(payload: number[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteSampleTypesQueryOptions(payload));
  }

  public setSampleLocking(sampleIds: number[], lockingAction: string): Promise<boolean> {
    return this.executeGraphQLMutation(() => getSetSampleLockingQueryOptions(sampleIds, lockingAction));
  }

  public deleteParentTypes(payload: number[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteParentTypesQueryOptions(payload));
  }

  public createUsers(users: InputUserAccountInput[]): Promise<any[]> {
    return this.executeGraphQLMutation(() => getCreateUsersQueryOptions(users));
  }

  public modifyUser(user: InputUserAccountInput): Promise<any> {
    return this.executeGraphQLMutation(() => getModifyUserQueryOptions(user));
  }

  public savePlaybook(playbookPayload: InputPlaybookInput): Promise<Playbook> {
    if (playbookPayload.playbookId) {
      return this.executeGraphQLMutation(() => getModifyPlaybookQueryOptions(playbookPayload));
    } else {
      return this.executeGraphQLMutation(() => getSaveNewPlaybookQueryOptions(playbookPayload));
    }
  }

  public invalidatePasswords(usernames: string[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getInvalidatePasswordsQueryOptions(usernames));
  }

  public changeYourPassword(payload: InputUserPasswordChangeRequestInput): Promise<boolean> {
    return this.executeGraphQLMutation(() => getChangeYourPasswordQueryOptions(payload));
  }

  public generateRestToken(lifetime: number): Promise<string> {
    return this.executeGraphQLMutation(() => generateRestToken(lifetime));
  }

  public installNCBIGenomes(input: InputGenomeAssembliesInput): Promise<boolean> {
    return this.executeGraphQLMutation(() => getInstallNcbiGenomes(input));
  }

  public downloadNCBIGenomeList(withStats: boolean): Promise<boolean> {
    return this.executeGraphQLMutation(() => getUpdateNcbiGenomeListOptions(withStats));
  }

  public checkSequencedOrganisms(tableQuerySpec: InputTableQuerySpec): Promise<boolean> {
    return this.executeGraphQLQuery(() => getCheckSequencedOrganismsOptions(tableQuerySpec));
  }

  public modifyProjectFile(fileInput: InputModifyProjectFileInput): Promise<ProjectFile> {
    return this.executeGraphQLMutation(() => getModifyProjectFileOptions(fileInput));
  }

  public indexBamFile(fileId: number): Promise<boolean> {
    return this.executeGraphQLMutation(() => getIndexBamFileOptions(fileId));
  }

  public saveOrgPropertyType(payload: InputOrganismPropertyTypeInput): Promise<OrganismPropertyType> {
    return this.executeGraphQLMutation(() => getSaveOrgPropertyTypeQueryOptions(payload));
  }

  public deleteOrgPropertyTypes(orgPropertyTypeIds: number[]): Promise<OrganismPropertyType> {
    return this.executeGraphQLMutation(() => getDeleteOrgPropertyTypesQueryOptions(orgPropertyTypeIds));
  }

  public editOrganism(input: InputModifyOrganismInput[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getEditOrganismQueryOptions(input));
  }

  public saveParent(editParentInput: InputEditParentInput): Promise<Parent> {
    return this.executeGraphQLMutation(() => getSaveParentQueryOptions(editParentInput));
  }

  public submitNextflowPlaybook(
    eventInputData: InputPlaybookEventInput,
    workflowParamInputValues: InputNfParamInput[],
    sampleEditingInput: InputEditSamplesInput,
  ): Promise<boolean> {
    return this.executeGraphQLMutation(() =>
      getSubmitNextflowPlaybookOptions(eventInputData, workflowParamInputValues, sampleEditingInput),
    );
  }

  public deleteUploadedSequences(sequenceIds: number[]): Promise<boolean> {
    return this.executeGraphQLMutation(() => getDeleteUploadedSequencesQueryOptions(sequenceIds));
  }

  public saveParamViewInput(viewInput: InputWorkflowViewParamInput): Promise<number> {
    return this.executeGraphQLMutation(() => getSaveParamViewInputOptions(viewInput));
  }
}
